import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Btn, Input, Field, card } from "./ui";
import { Loader2 } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // Handle Google OAuth callback (session_id in URL fragment)
  useEffect(() => {
    const hash = location.hash;
    if (hash && hash.includes("session_id=")) {
      const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
      if (sid) {
        (async () => {
          setBusy(true);
          try {
            const { data } = await api.post("/auth/google/session", { session_id: sid });
            login(data.token, data.user);
            window.history.replaceState(null, "", "/admin");
            navigate("/admin", { replace: true });
          } catch (e) {
            setError("Google sign-in failed.");
          } finally { setBusy(false); }
        })();
      }
    }
  }, [location.hash]);

  useEffect(() => { if (user && user.email) navigate("/admin", { replace: true }); }, [user]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.token, data.user);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setBusy(false); }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[#1C1210] grain flex items-center justify-center p-4">
      <div className={`${card} p-8 sm:p-10 w-full max-w-md relative`}>
        <div className="text-center mb-8">
          <img src="/logo.jpeg" alt="Foodie Connect" className="h-16 mx-auto rounded-md mb-4" />
          <p className="text-gold/70 text-[0.68rem] tracking-[0.3em] uppercase">Operexa DineOS</p>
          <h1 className="font-serif text-3xl text-ivory mt-1">Operations Login</h1>
        </div>
        <form onSubmit={submit}>
          <Field label="Email"><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" required data-testid="login-email" /></Field>
          <Field label="Password"><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required data-testid="login-password" /></Field>
          {error && <p className="text-red-400 text-sm mb-4" data-testid="login-error">{error}</p>}
          <Btn type="submit" className="w-full justify-center" disabled={busy} data-testid="login-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
          </Btn>
        </form>
        <div className="flex items-center gap-3 my-5"><span className="h-px flex-1 bg-gold/15" /><span className="text-ivory/30 text-xs">or</span><span className="h-px flex-1 bg-gold/15" /></div>
        <Btn variant="outline" className="w-full justify-center" onClick={googleLogin} data-testid="login-google">
          <i className="fa-brands fa-google" /> Continue with Google
        </Btn>
        <p className="text-center text-ivory/30 text-xs mt-6">Authorised staff only · Foodie Connect Caterer</p>
      </div>
    </div>
  );
}
